<template>
    <Head title="IT Equipment Categories" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!--  Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">IT Equipment Categories</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Organize your IT equipment with hierarchical categories</p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Link
                            :href="route('it-equipment.index')"
                            class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Equipment
                        </Link>
                        <Link
                            v-if="$page.props.auth.permissions.includes('it_helpdesk.assets.create')"
                            :href="route('it-equipment-categories.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Category
                        </Link>
                    </div>
                </div>
            </div>

            <!-- Categories List -->
            <div class="bg-white dark:bg-secondary-800 shadow rounded-lg overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-secondary-700">
                    <input
                        v-model="searchQuery"
                        @input="handleSearch"
                        type="text"
                        placeholder="Search categories..."
                        class="w-full max-w-md rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>

                <div v-if="categories.length > 0" class="divide-y divide-gray-200 dark:divide-secondary-700">
                    <CategoryItem
                        v-for="category in categories"
                        :key="category.id"
                        :category="category"
                        :level="0"
                    />
                </div>

                <div v-else class="p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No categories found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Get started by creating a new category.</p>
                    <div class="mt-6">
                        <Link
                            v-if="$page.props.auth.permissions.includes('it_helpdesk.assets.create')"
                            :href="route('it-equipment-categories.create')"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Category
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
    categories: Array,
    filters: Object,
});

const searchQuery = ref(props.filters.search || '');

let searchTimeout = null;

function handleSearch() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        router.get(route('it-equipment-categories.index'), {
            search: searchQuery.value,
        }, {
            preserveState: true,
            preserveScroll: true,
        });
    }, 500);
}

// Category Item Component (inline for simplicity)
const CategoryItem = {
    name: 'CategoryItem',
    props: {
        category: Object,
        level: Number,
    },
    components: {
        Link,
    },
    setup() {
        const { props: pageProps } = usePage();
        return {
            route: route, // Make route available in template
            $page: { props: pageProps },
        };
    },
    template: `
        <div>
            <div class="px-6 py-4 hover:bg-gray-50 dark:hover:bg-secondary-750" :style="{ paddingLeft: (level * 2 + 1.5) + 'rem' }">
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div class="flex items-center gap-3">
                            <svg v-if="level > 0" class="w-4 h-4 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                            <div>
                                <h3 class="text-sm font-medium text-gray-900 dark:text-white">{{ category.name }}</h3>
                                <p v-if="category.description" class="mt-1 text-sm text-gray-500 dark:text-secondary-400">{{ category.description }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center space-x-3">
                        <span class="text-sm text-gray-500 dark:text-secondary-400">
                            {{ category.equipment_count || 0 }} equipment
                        </span>
                        <span
                            :class="category.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300'"
                            class="px-2 py-1 text-xs font-medium rounded-full"
                        >
                            {{ category.is_active ? 'Active' : 'Inactive' }}
                        </span>
                        <Link
                            v-if="$page.props.auth.permissions.includes('it_helpdesk.assets.edit')"
                            :href="route('it-equipment-categories.edit', category.id)"
                            class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                        >
                            Edit
                        </Link>
                    </div>
                </div>
            </div>
            <template v-if="category.children_data && category.children_data.length > 0">
                <CategoryItem
                    v-for="child in category.children_data"
                    :key="child.id"
                    :category="child"
                    :level="level + 1"
                />
            </template>
        </div>
    `,
};
</script>
